import {
  ArrowUpRight,
  ClockWatch,
  PenEdit,
  RecordVideoCamera,
  Repeat,
} from '@griply/assets';
import {
  CalendarEventDetails,
  CalendarEventFormFields,
  CalendarEventResponseStatus,
} from '@griply/types';
import {
  getCalendarEventGuestList,
  getCalendarEventRepeatDisplay,
} from '@griply/utils';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Colors, Sizes, Variants } from 'shared/components/ui/button';
import {
  CustomFormSelect,
  Size as SelectSize,
} from 'shared/components/ui/custom-form-select';
import { Divider } from 'shared/components/ui/divider';
import { useToday } from 'shared/contexts/today';
import {
  DateFormatOptions,
  TimeFormatOptions,
} from 'shared/types/date-format-options';
import { WeekDays } from 'shared/types/week-days';

import { EventFormHeader } from './event-form-header';
import * as Styled from './event-read-only-view.style';
import { EventRow } from './event-row';
import { EventDateTimeRow } from './rows/event-date-time-row';
import { EventDescriptionRow } from './rows/event-description-row';
import { EventGuestsRow } from './rows/event-guests-row';
import { EventLocationRow } from './rows/event-location-row';
import { EventVideoCallRow } from './rows/event-video-call-row';

export type EventReadOnlyViewProps = {
  fields: CalendarEventFormFields;
  dateFormat: DateFormatOptions;
  timeFormat: TimeFormatOptions;
  weekStartsOn: WeekDays;
  isLoadingDetails?: boolean;
  // an instance carries no rrule of its own, so the repeat row reads these, not `fields.rrule`
  isRecurring?: boolean;
  seriesRrule?: string | null;
  videoCallUrl?: string | null;
  // the conferencing system's own label ("Zoom Meeting"); read-only here — an invitee cannot
  // change the event, so the row names the call without offering to replace it
  videoCallProviderName?: string | null;
  descriptionHtml?: string | null;
  // Outlook omits the organizer from `guests`, so the display list merges them back in
  organizer?: CalendarEventDetails['organizer'];
  isOrganizer?: CalendarEventDetails['isOrganizer'];
  responseStatus?: CalendarEventResponseStatus | null;
  isResponding?: boolean;
  hasRespondError?: boolean;
  // neither provider accepts a counter-proposal through its API, so proposing hands over to theirs
  proposeTimeUrl?: string;
  // responding is a write like any other, so it is gated with editing
  isEditLocked?: boolean;
  onRespond: (status: CalendarEventResponseStatus) => void;
  // absent when the backend sent no provider url, which is what hides the button
  onOpenInCalendar?: () => void;
  onUnlockEdit: () => void;
};

export const EventReadOnlyView: React.FC<EventReadOnlyViewProps> = ({
  fields,
  dateFormat,
  timeFormat,
  weekStartsOn,
  isLoadingDetails,
  isRecurring,
  seriesRrule,
  videoCallUrl,
  videoCallProviderName,
  descriptionHtml,
  organizer,
  isOrganizer,
  responseStatus,
  isResponding,
  hasRespondError,
  proposeTimeUrl,
  isEditLocked,
  onRespond,
  onOpenInCalendar,
  onUnlockEdit,
}) => {
  const { t } = useTranslation();
  const today = useToday();

  const guestList = useMemo(
    () =>
      getCalendarEventGuestList({
        guests: fields.guests,
        organizer,
        isOrganizer,
      }),
    [fields.guests, organizer, isOrganizer],
  );

  // the pattern names the series; an invitee edits nothing, so an unnameable rule shows no row
  const { label: repeatLabel } = useMemo(
    () =>
      getCalendarEventRepeatDisplay({
        rrule: fields.rrule,
        seriesRrule,
        isRecurring,
        today,
        t,
        weekStartsOn,
      }),
    [fields.rrule, seriesRrule, isRecurring, today, t, weekStartsOn],
  );

  const responseOptions = useMemo(
    () => [
      {
        label: t('forms.event.rsvp.options.yes'),
        value: CalendarEventResponseStatus.Accepted,
      },
      {
        label: t('forms.event.rsvp.options.no'),
        value: CalendarEventResponseStatus.Declined,
      },
      {
        label: t('forms.event.rsvp.options.maybe'),
        value: CalendarEventResponseStatus.Tentative,
      },
    ],
    [t],
  );

  const onChangeResponse = (value?: string) =>
    value && onRespond(value as CalendarEventResponseStatus);

  const onProposeNewTime = () =>
    window.open(proposeTimeUrl, '_blank', 'noreferrer');

  // read-only now opens for solo events too, so an empty guest list is an ordinary state
  const hasGuestRows = !!guestList.length || !!isLoadingDetails;

  const hasDetailRows =
    !!videoCallUrl || !!fields.location || !!isLoadingDetails;

  return (
    <Styled.Container>
      <EventFormHeader value={fields.title} disabled />
      <Divider noMargin />
      <Styled.ScrollArea>
        <Styled.Body>
          <Styled.Section>
            {fields.allDay && (
              <EventRow icon={ClockWatch}>
                <Styled.ReadOnlyValue>
                  {t('general.labels.all-day')}
                </Styled.ReadOnlyValue>
              </EventRow>
            )}

            <EventDateTimeRow
              label={t('forms.event.fields.starts.label')}
              date={fields.startDate}
              time={fields.startTime}
              allDay={fields.allDay}
              today={today}
              weekStartsOn={weekStartsOn}
              dateFormat={dateFormat}
              timeFormat={timeFormat}
              timePlaceholder={t('forms.event.fields.starts.placeholder')}
              disabled
            />

            <EventDateTimeRow
              label={t('forms.event.fields.ends.label')}
              date={fields.endDate}
              time={fields.endTime}
              allDay={fields.allDay}
              today={today}
              weekStartsOn={weekStartsOn}
              dateFormat={dateFormat}
              timeFormat={timeFormat}
              timePlaceholder={t('forms.event.fields.ends.placeholder')}
              disabled
            />

            {!!repeatLabel && (
              <EventRow icon={Repeat}>
                <Styled.ReadOnlyValue>{repeatLabel}</Styled.ReadOnlyValue>
              </EventRow>
            )}

            {!isEditLocked && !!proposeTimeUrl && (
              <EventRow>
                <Button
                  variant={Variants.Outlined}
                  size={Sizes.Small}
                  color={Colors.Inherit}
                  endIcon={ArrowUpRight}
                  onClick={onProposeNewTime}
                  type="button"
                >
                  {t('forms.event.propose-time.trigger')}
                </Button>
              </EventRow>
            )}
          </Styled.Section>
        </Styled.Body>

        {hasGuestRows && (
          <>
            <Divider noMargin />
            <Styled.Body>
              <Styled.Section>
                <EventGuestsRow
                  value={guestList}
                  isLoading={isLoadingDetails}
                  disabled
                />
              </Styled.Section>
            </Styled.Body>
          </>
        )}

        {hasDetailRows && (
          <>
            <Divider noMargin />
            <Styled.Body>
              <Styled.Section>
                {!!videoCallUrl && (
                  <EventVideoCallRow
                    url={videoCallUrl}
                    providerName={videoCallProviderName}
                    icon={RecordVideoCamera}
                    showJoinButton
                  />
                )}
                {(!!fields.location || !!isLoadingDetails) && (
                  <EventLocationRow
                    value={fields.location}
                    isLoading={isLoadingDetails}
                    disabled
                  />
                )}
              </Styled.Section>
            </Styled.Body>
          </>
        )}

        {!!fields.description && (
          <>
            <Divider noMargin />
            <Styled.Body>
              <Styled.Section>
                <EventDescriptionRow
                  value={fields.description}
                  html={descriptionHtml}
                  disabled
                />
              </Styled.Section>
            </Styled.Body>
          </>
        )}
      </Styled.ScrollArea>

      <Divider noMargin />

      {isEditLocked ? (
        <Styled.RsvpRow>
          <Button
            variant={Variants.Contained}
            size={Sizes.Small}
            color={Colors.White}
            startIcon={PenEdit}
            onClick={onUnlockEdit}
            type="button"
          >
            {t('general.edit')}
          </Button>
          {!!onOpenInCalendar && (
            <Button
              variant={Variants.Contained}
              size={Sizes.Small}
              color={Colors.White}
              endIcon={ArrowUpRight}
              onClick={onOpenInCalendar}
              type="button"
            >
              {t('forms.event.open-in-calendar')}
            </Button>
          )}
        </Styled.RsvpRow>
      ) : (
        <Styled.RsvpRow>
          <span>{t('forms.event.rsvp.label')}</span>
          <Styled.RsvpControl>
            <CustomFormSelect
              value={responseStatus ?? undefined}
              options={responseOptions}
              onChange={onChangeResponse}
              placeholder={t('forms.event.rsvp.placeholder')}
              size={SelectSize.ExtraSmall}
              disabled={isResponding || isLoadingDetails}
            />
          </Styled.RsvpControl>
        </Styled.RsvpRow>
      )}

      {!isEditLocked && !!hasRespondError && (
        <Styled.ErrorText role="alert">
          {t('forms.event.rsvp.error')}
        </Styled.ErrorText>
      )}
    </Styled.Container>
  );
};
